import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';

import {BaseService} from '../services/base.service';
import {User} from '../shared/models/user/user';
import { environment } from '../../environments/environment';
import { UserList } from '../../swagger-gen/web-secured/models/user-list';
import { UserDiffusionList } from '../models/users/user-diffusion-list';
import { UserCreate } from '../../swagger-gen/web-secured/models/user-create';
import { UserUpdate } from '../../swagger-gen/web-secured/models/user-update';
import { DiffusionListSearchBody } from '../../swagger-gen/web-secured/models/diffusion-list-search-body';
import { DiffusionListPaged } from '../../swagger-gen/web-secured/models/diffusion-list-paged';
import { SingleDiffusionList } from '../../swagger-gen/web-secured/models/single-diffusion-list';
import { DiffusionListUpdate } from '../../swagger-gen/web-secured/models/diffusion-list-update';
import { UserSummary } from '../../swagger-gen/gnvert-secured/models/user-summary';
import { UserSearch } from '../../swagger-gen/web-secured/models/user-search';

@Injectable({
    providedIn: 'root',
})
export class UserRepo extends BaseService {
    userUrl = environment.apiUrl + '/users';
    readonly USERS_LIMIT = '20';

    constructor(private http: HttpClient, private logger: NGXLogger) {
        super();
    }

    getUsersList(offset: number,
                 limit: number,
                 params: any[]): Observable<UserList> {
        const searchValue = params.find(p => p.key === 'search') ? params.find(p => p.key === 'search').value : null;
        if (searchValue !== null && searchValue !== undefined && searchValue.trim() !== '') {
            return this.searchUsers(offset, limit, params);
        } else {
            return this.getUsers(offset, limit, params);
        }
    }

    initHttpOptions(
        offset: number,
        limit: number,
        params: any[],
    ) {
        let requestString = this.pagination(offset, limit, this.USERS_LIMIT);
        let queryParams = '';
        params.forEach((p: any) => {
            queryParams += '&' + p.key + '=' + p.value;
        });
        requestString += queryParams;
        const requestParams = new HttpParams({fromString: requestString});
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            params: requestParams,
        };
    }

    getUser(uid: string): Observable<User> {
        const url = this.userUrl + `/${uid}`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .get<User>(url, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    createUser(userCreate: UserCreate) {
        const url = this.userUrl;
        const httpOptions: any = {
            headers: this.httpHeaders(),
            observe: 'response',
        };

        return this.http
            .post<any>(url, userCreate, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    updateUser(uid: string, userUpdate: UserUpdate) {
        const url = this.userUrl + `/${uid}`;
        const httpOptions = {
            headers: this.httpHeaders(),
        };

        return this.http
            .put<any>(url, userUpdate, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    /**
     * Assign an user to app
     * @param body object to send
     */
    assignUser(body: any): Observable<any> {
        const url = `${this.userUrl}/assign`;
        const httpOptions: any = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http.post<any>(url, body, httpOptions).pipe(
            tap((response) => {
                this.logger.log(`User assigning response`, response);
            }), // DEBUG
            catchError(this.handleServerError)
        );
    }

    getDiffusionList(
        offset: number,
        limit: number,
        params: any[],
        body: DiffusionListSearchBody
    ): Observable<DiffusionListPaged> {
        const url = `${this.userUrl}/diffusionlist/search`;
        let requestString = this.pagination(offset, limit, '20');
        let queryParams = '';
        params.forEach((p: any) => {
            queryParams += '&' + p.key + '=' + p.value;
        });
        requestString += queryParams;
        const requestParams = new HttpParams({fromString: requestString});
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            params: requestParams,
        };

        this.logger.log('filter body ', body);
        return this.http
            .post<DiffusionListPaged>(url, body, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    retrieveDiffusionList(id: string): Observable<UserDiffusionList> {
        const url = `${this.userUrl}/diffusionlist/${id}`;
        return this.http.get<SingleDiffusionList>(url, {
            headers: this.httpHeaders()
        }).pipe(
            map((singleDiffusionList) => ({
                id: singleDiffusionList.uid,
                name: singleDiffusionList.name
            })),
            catchError(this.handleServerError)
        );
    }

    retrieveAllDiffusionList(): Observable<DiffusionListPaged> {
        const url = `${this.userUrl}/diffusionlist/search`;
        return this.http
            .post<DiffusionListPaged>(url, {}, {
                headers: this.httpHeaders()
            })
            .pipe(catchError(this.handleServerError));
    }

    createDiffusionList(body: DiffusionListUpdate): Observable<any> {
        const url = `${this.userUrl}/diffusionlist`;

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .post<any>(url, body, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    updateDiffusionList(uid: string, body: DiffusionListUpdate): Observable<any> {
        const url = `${this.userUrl}/diffusionlist/${uid}`;

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .put<any>(url, body, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    deleteDiffusionList(uid: string) {
        const url = `${this.userUrl}/diffusionlist/${uid}`;

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .delete<any>(url, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    subscribeusers(userUid: string, subPlanUid: string): Observable<any> {
        const url = `${this.userUrl}/${userUid}/subscribe/${subPlanUid}`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .post<any>(url, null, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    unsubscribeuser(userUid: string, subPlanUid: string): Observable<any> {
        const url = `${this.userUrl}/${userUid}/unsubscribe/${subPlanUid}`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .post<any>(url, null, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    /**
     * Subscribe user to default plan
     * @param uid user uid
     */
    subscribeUserToDefaultPlan(uid: string): Observable<any> {
        const url = `${this.userUrl}/${uid}/subscribe`;
        const httpOptions: any = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            observe: 'response',
        };

        return this.http.post<any>(url, null, httpOptions).pipe(
            tap((response) => {
                this.logger.log(`User subscription response`, response);
            }), // DEBUG
            catchError(this.handleServerError)
        );
    }

    exportUserDefinition(): Observable<any> {
        const url = `${this.userUrl}/export`;

        const httpOptions: any = {
            headers: this.httpHeaders(),
            responseType: 'blob' as 'json',
            observe: 'response',
        };

        return this.http
            .get<any>(url, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    importUserDefinition(file: any): Observable<any> {
        const url = `${this.userUrl}/import`;

        const httpOptions: any = {
            headers: new HttpHeaders().set(
                'Content-Type',
                'application/vnd.ms-excel'
            ),
            observe: 'response',
        };

        return this.http
            .post<any>(url, file, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    deleteUser(uid: string): Observable<any> {
        const url = this.userUrl + '/' + uid;

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
        };

        return this.http
            .delete<any>(url, httpOptions)
            .pipe(catchError(this.handleServerError));
    }

    getUserByLogin(login: string): Observable<UserSummary | null> {
        const url = `${this.userUrl}/findByLogin`;
        return this.http.post<UserSummary>(url, {login}, {
            headers: this.httpHeaders()
        }).pipe(
            catchError((httpErrorResponse: HttpErrorResponse) => {
                if (httpErrorResponse.error.code === '50.004') {
                    return of(null);
                }

                return throwError(() => httpErrorResponse);
            })
        );
    }

    /**
     * Get all users
     */
    private searchUsers(
        offset: number,
        limit: number,
        params: any[],
        userBody?: UserSearch
    ): Observable<UserList> {
        const url = this.userUrl + '/search';
        let body = userBody;
        if (!userBody) {
            body = {
                types: [],
                isActive: true,
                withoutSubscription: false,
            };
        }

        return this.http
            .post<UserList>(url, body, this.initHttpOptions(offset, limit, params))
            .pipe(catchError(this.handleServerError));
    }

    /**
     * Get user by uid
     */
    private getUsers(
        offset: number,
        limit: number,
        params: any[]): Observable<UserList> {
        const url = this.userUrl;
        return this.http
            .get<UserList>(url, this.initHttpOptions(offset, limit, params))
            .pipe(catchError(this.handleServerError));
    }

}
